// src/services/credentialStore.js
//
// Single owner of the `localStorage["matrixCreds"]` session blob (F-04 hardening).
//
// Why this module exists: the access + refresh tokens live in JS-readable
// localStorage (there is no XSS-proof token store in a pure SPA that talks
// directly to a cross-origin, federated homeserver). The pentest's mitigation
// is to (a) reduce script-injection probability via CSP/SRI (F-01/F-02) and
// (b) SHRINK BLAST RADIUS by funneling every read/write/clear of the blob
// through one place. With a single owner, a future swap (in-memory access
// token, or a BFF) is a localized change here, not an 11-site edit.
//
// Standalone (imports only `logger`) so neither matrixService nor
// matrixAuthService has to import the other — avoids the circular import that
// a method on either service would create.
//
// SECURITY: this module logs only messages/booleans — NEVER token values.
//
// Two readers, by design:
//   - readCredentials():  raw parse, NO field validation. For call sites that
//     only need one field (homeserverUrl / accessToken / deviceId) and must
//     tolerate a partial blob (e.g. customFetch / uploadMedia fallbacks, whose
//     unit tests seed `{accessToken}`-only blobs).
//   - getCredentials():   validated (requires accessToken+userId+homeserverUrl);
//     auto-clears an invalid/unparseable blob with a LIGHTWEIGHT removeItem.
//     Backs matrixAuthService.getStoredCredentials().

import logger from '../utils/logger';

const STORAGE_KEY = 'matrixCreds';

/**
 * Raw read of the stored credentials blob.
 * Parses JSON but performs NO field validation. On an unparseable blob the
 * (useless) value is removed — mirroring the per-site try/catch cleanup this
 * module replaces. Returns the parsed object, or null if missing/unparseable.
 * @returns {Object|null}
 */
function readCredentials() {
  let raw;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode / disabled). Nothing to read.
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    // An unparseable blob can never yield a usable session — drop it.
    logger.warn('credentialStore: stored credentials unparseable, removing');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore — best-effort cleanup
    }
    return null;
  }
}

/**
 * Validated read of the stored credentials blob.
 * Requires accessToken, userId, and homeserverUrl (the same invariant the
 * legacy matrixAuthService.getStoredCredentials() enforced). On a missing,
 * unparseable, or incomplete blob, the value is removed (lightweight
 * removeItem — NOT the heavyweight matrixAuthService.clearCredentials() side
 * effects; those stay with explicit logout) and null is returned.
 * @returns {Object|null}
 */
function getCredentials() {
  const credentials = readCredentials();
  if (!credentials) {
    // Missing or unparseable (readCredentials already cleaned an unparseable one).
    return null;
  }
  if (!credentials.accessToken || !credentials.userId || !credentials.homeserverUrl) {
    logger.warn('credentialStore: stored credentials missing required fields, removing');
    clearCredentials();
    return null;
  }
  return credentials;
}

/**
 * Persist the credentials blob.
 * @param {Object} credentials
 * @throws {Error} if the write fails (e.g. quota) — callers rely on this to
 *   surface a "could not save login" failure, matching the prior behavior of
 *   matrixAuthService.storeCredentials().
 */
function setCredentials(credentials) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
    logger.debug('credentialStore: credentials stored');
  } catch (error) {
    logger.error('credentialStore: failed to store credentials', error);
    throw new Error('Failed to save login credentials');
  }
}

/**
 * Remove the credentials blob (lightweight — no muted-cache / unread-floor
 * side effects; those live in matrixAuthService.clearCredentials() for the
 * explicit logout path).
 */
function clearCredentials() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    logger.debug('credentialStore: credentials cleared');
  } catch (error) {
    logger.error('credentialStore: failed to clear credentials', error);
  }
}

/**
 * Raw presence check of the stored blob (truthy of the stored string). Does
 * NOT parse or validate, and has NO side effects — use for "is there any
 * residual session to clean up" guards where even a partial/corrupt blob
 * should count.
 * @returns {boolean}
 */
function hasCredentials() {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export {
  STORAGE_KEY,
  readCredentials,
  getCredentials,
  setCredentials,
  clearCredentials,
  hasCredentials,
};
